import { Flex, Spinner, Text, VStack } from '@chakra-ui/react';
import { FC } from 'react';
import { DocumentActionButtons } from './DocumentActionButtons';
import { DocumentPlatformRequirementIssuesList } from './DocumentPlatformRequirementIssuesList';
import { DocumentSuggestionsList } from './DocumentSuggestionsList';
import { SuccessfulChecksList } from './SuccessfulChecksList';
import {
  CompliancePlatformRequirementIssue,
  ComplianceSuccessfulCheck,
  ComplianceSuggestion,
} from './types';

interface RightRailProps {
  suggestions: ComplianceSuggestion[];
  platformRequirementIssues: CompliancePlatformRequirementIssue[];
  successfulChecks: ComplianceSuccessfulCheck[];
  hasSelection: boolean;
  /**
   * True when the selected document has open suggestions. The document-level
   * action buttons (Ignore and resubmit / Fix in SkySlope) only show for
   * needs-review documents; clean documents have nothing to resubmit.
   */
  needsReview: boolean;
  loading: boolean;
  /** True until the per-document detail has loaded for the selection. */
  hasDetail: boolean;
  /** Called with a row's field ids when a suggestion/check row is clicked. */
  onSelectFields: (fieldIds: string[]) => void;
  /** Called with a suggestion to hide it (document-level ignore). */
  onIgnoreSuggestion: (suggestion: ComplianceSuggestion) => void;
  /** Called with an already-ignored suggestion to un-hide it (Undo). */
  onUndoIgnoreSuggestion: (suggestion: ComplianceSuggestion) => void;
  /** Called with a checklist mismatch to hide it. */
  onIgnorePlatformRequirementIssue: (
    issue: CompliancePlatformRequirementIssue
  ) => void;
  /** Called with an already-ignored checklist mismatch to un-hide it. */
  onUndoIgnorePlatformRequirementIssue: (
    issue: CompliancePlatformRequirementIssue
  ) => void;
  /** True while a document-level ignore mutation is in flight. */
  isIgnoring: boolean;
  /** True while a checklist mismatch ignore mutation is in flight. */
  isIgnoringPlatformRequirementIssue: boolean;
  /** Fires the resubmit action for the selected document with the agent's note. */
  onResubmit: (message: string) => void | Promise<void>;
  /**
   * The note the resubmit modal is prepopulated with — the crossed-out
   * (ignored) suggestion messages for the selected document.
   */
  defaultResubmitMessage: string;
  /** True once the selected document has already been resubmitted. */
  isResubmitted: boolean;
  /** True while the resubmit mutation is in flight. */
  isSubmitting: boolean;
  /** SkySlope (or other platform) deep link for the folder/document. */
  platformUrl?: string | null;
}

/**
 * Document-level actions, suggestions, checklist mismatches, and passing checks
 * for the selected document (derived client-side from the raw detail). Shows a
 * spinner while the detail loads and a muted placeholder when nothing is
 * selected (or the selected document has no findings).
 *
 * Shared by the desktop right rail and the mobile detail view (stacked below
 * the PDF). Clicking a suggestion/check row calls `onSelectFields(row.fieldIds)`,
 * which the controller lifts into `activeFieldId` to scroll/center the PDF on
 * the field.
 */
export const RightRail: FC<RightRailProps> = ({
  suggestions,
  platformRequirementIssues,
  successfulChecks,
  hasSelection,
  needsReview,
  loading,
  hasDetail,
  onSelectFields,
  onIgnoreSuggestion,
  onUndoIgnoreSuggestion,
  onIgnorePlatformRequirementIssue,
  onUndoIgnorePlatformRequirementIssue,
  isIgnoring,
  isIgnoringPlatformRequirementIssue,
  onResubmit,
  defaultResubmitMessage,
  isResubmitted,
  isSubmitting,
  platformUrl,
}) => {
  if (!hasSelection) {
    return (
      <Text color="compliance.text.muted" fontSize="sm">
        Select a document to see its suggestions and checks.
      </Text>
    );
  }

  const hasFindings =
    suggestions.length > 0 ||
    platformRequirementIssues.length > 0 ||
    successfulChecks.length > 0;

  return (
    <VStack align="stretch" spacing={5}>
      {/* Document-level actions sit above the suggestions (per Figma). The
          resubmit action shows for needs-review / already-resubmitted
          documents; the SkySlope link shows for any document (labeled "Fix" vs
          "Open" by status). DocumentActionButtons renders nothing when neither
          applies. */}
      <DocumentActionButtons
        defaultResubmitMessage={defaultResubmitMessage}
        isResubmitReady={hasDetail}
        // Gate only the resubmit trigger (not the overview-derived needsReview
        // signal / SkySlope label) on detail being loaded: the resubmit payload
        // is derived from detail (validation id + open suggestions), so the
        // modal must not open before that data exists.
        isResubmitted={isResubmitted}
        isSubmitting={isSubmitting}
        needsReview={needsReview}
        platformUrl={platformUrl}
        onResubmit={onResubmit}
      />

      {loading && !hasDetail ? (
        <Flex justify="center" py={8}>
          <Spinner color="compliance.loading.text" />
        </Flex>
      ) : hasFindings ? (
        <>
          <DocumentSuggestionsList
            isIgnoring={isIgnoring}
            suggestions={suggestions}
            onIgnore={onIgnoreSuggestion}
            onSelectFields={onSelectFields}
            onUndoIgnore={onUndoIgnoreSuggestion}
          />
          <DocumentPlatformRequirementIssuesList
            isIgnoring={isIgnoringPlatformRequirementIssue}
            issues={platformRequirementIssues}
            onIgnore={onIgnorePlatformRequirementIssue}
            onUndoIgnore={onUndoIgnorePlatformRequirementIssue}
          />
          <SuccessfulChecksList
            checks={successfulChecks}
            onSelectFields={onSelectFields}
          />
        </>
      ) : (
        <Text color="compliance.text.muted" fontSize="sm">
          No validation suggestions, checklist suggestions, or checks for this
          document.
        </Text>
      )}
    </VStack>
  );
};
